import {
  Collapsible,
  Icon,
  IconButton,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { LogEntry as LogEntryT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { PropsWithChildren } from 'lib-shared-types-utils';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';

import styles from './logEntry.module.scss';
import { isAutomaticEvent } from './logEntry.utils';

export type EntryProps = {
  entry: LogEntryT;
  icon?: IconNames;
  isExpandable?: boolean;
};

type LogEntryProps = {
  text: ReactNode;
} & EntryProps &
  PropsWithChildren;

export const LogEntry = ({
  entry,
  text,
  children,
  isExpandable = false,
  icon,
}: LogEntryProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(!isExpandable);
  const eventTypeIcon = isAutomaticEvent(entry) ? 'cog' : 'user';

  useEffect(() => {
    if (isExpanded) {
      ampli.conversationActivityLogFlooterExpanded();
    }
  }, [isExpanded]);

  return (
    <div className={styles.root}>
      <div className={styles.main}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <Icon name={icon || eventTypeIcon} size={16} />
          </div>
          <Text.Paragraph variant="3" className={styles.text}>
            {text}
          </Text.Paragraph>
          {isExpandable && (
            <IconButton
              onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
              icon={isExpanded ? 'angle-up' : 'angle-down'}
              variant="tertiary"
            />
          )}
        </div>
        <ConversationTimestamp
          shouldDateFormatPersist={true}
          time={entry.event_timestamp}
          isSecondsDisplayed={false}
        />
      </div>
      <Collapsible isOpen={isExpanded} header={null}>
        {children}
      </Collapsible>
    </div>
  );
};
